import * as Fields from "#root/components/form/fields";
import StandardEditor from "#root/components/standard-editor";
import type { RecordModificationResponse } from "#root/utility/remote-data";
import { regex } from "#root/utility/validators";
import type { ReactNode } from "react";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  newsRPMNetworkCollection,
  type NewsRPMNetworkDetailItem,
  NewsRPMNetworkDetailItemSchema,
  NewsRPMNetworkListItemSchema
} from "#root/schema/platform/newsrpm-marketplace";
import PageLayout from "./layout";

export default function ({ networkCode }): ReactNode {
  const navigate = useNavigate();
  const handleUpdate = useCallback(
    (data: RecordModificationResponse<NewsRPMNetworkDetailItem>) => {
      if ("deleted" in data) {
        navigate("/admin/platform/newsrpm-marketplace/network");
      } else if ("created" in data) {
        navigate(`/admin/platform/newsrpm-marketplace/network/${data.created}`);
      }
    },
    [navigate],
  );

  return (
    <PageLayout title="NewsRPM Marketplace - Edit Network">
      <section>
        <h2>{networkCode === "new" ? "Create" : "Edit"} Network</h2>
        <StandardEditor<typeof NewsRPMNetworkListItemSchema, typeof NewsRPMNetworkDetailItemSchema>
          id={networkCode}
          collection={newsRPMNetworkCollection}
          onSave={handleUpdate}
          onDelete={handleUpdate}
        >
          <Fields.Set name="code">
            <label>Network Code</label>
            <Fields.String required validator={regex(/^[a-zA-Z\d]*$/, "Lowercase letters and numbers only, no spaces or special characters.")}/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="name">
            <label>Network Name</label>
            <Fields.String required/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="deploymentId">
            <label>Deployment ID</label>
            <Fields.Number required/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="sampleURI">
            <label>Sample URL</label>
            <Fields.Domain required/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="pressReleasePrice">
            <label>Price per Press Release</label>
            <Fields.Currency/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="siteCount">
            <label>Number of sites</label>
            <Fields.Number/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="minimumMonthlyEarnBeforeRevenueShare">
            <label>Minimum monthly earnings before revenue share kicks in</label>
            <Fields.Currency/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="revenueShare">
            <label>Revenue Share %</label>
            <Fields.Number/>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="available">
            <label>Listing Status</label>
            <Fields.BooleanSelect>
              <Fields.Option value={0}>Not Listed</Fields.Option>
              <Fields.Option value={1}>Listed</Fields.Option>
            </Fields.BooleanSelect>
            <Fields.Validity/>
          </Fields.Set>
          <Fields.Set name="description">
            <label>Network Profile</label>
            <Fields.TextArea rows={100}/>
            <Fields.Validity/>
          </Fields.Set>
        </StandardEditor>
      </section>
    </PageLayout>
  );
}
